import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FARE_TTL_MS } from './useFareCountdown';
import {
  SearchResultGenerationAuthority,
  searchResultGenerationAuthorityAt,
  searchSelectionBlockedReasonKey,
} from './searchResultAuthority';

interface SearchObserverGeneration {
  data?: unknown;
  dataUpdatedAt: number;
  isFetching: boolean;
  isStale: boolean;
}

/**
 * One expiry timer per mounted search generation, never one per result card.
 *
 * `formDiverged` es obligatorio: la divergencia se deriva por vertical (cada
 * formulario tiene sus campos) pero se COMPARA con una sola definición
 * (`searchFormDiverged`), y un buscador que se olvide de pasarla no compila.
 */
export function useSearchResultAuthority(
  search: SearchObserverGeneration,
  formDiverged: boolean,
): SearchResultGenerationAuthority {
  const hasData = Boolean(search.data);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const currentNow = Date.now();
    setNow(currentNow);

    if (!hasData || !Number.isFinite(search.dataUpdatedAt) || search.dataUpdatedAt <= 0) {
      return undefined;
    }

    const remainingMs = search.dataUpdatedAt + FARE_TTL_MS - currentNow;
    if (remainingMs <= 0) return undefined;

    const timer = setTimeout(() => setNow(Date.now()), remainingMs);
    return () => clearTimeout(timer);
  }, [hasData, search.dataUpdatedAt]);

  return searchResultGenerationAuthorityAt(
    {
      hasData,
      dataUpdatedAt: search.dataUpdatedAt,
      isFetching: search.isFetching,
      isStale: search.isStale,
      formDiverged,
    },
    now,
  );
}

/** El motivo del bloqueo, ya traducido, para pegarlo al botón deshabilitado. */
export function useSearchSelectionBlockedReason(authority: SearchResultGenerationAuthority): string | undefined {
  const { t } = useTranslation();
  const key = searchSelectionBlockedReasonKey(authority);
  return key ? t(key) : undefined;
}
